import type { TFunction } from 'i18next';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import styles from './StockEntryDialog.module.css';

interface IStockEntrySpanFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Whether the field should render amber — precomputed by the parent
   *  (seeded-value match, or a still-driving derived sync span). */
  highlighted: boolean;
  /** Under Sync the span is derived, not seeded, so the normal "Estimated N"
   *  override note can't come from the seed map. The parent passes the derived
   *  value here once the user moves the field off it; null otherwise. */
  estimatedSpan: number | null;
  ratePreview: number | null;
  unitLabel: string;
  lead: number | null;
  unsustainable: boolean;
  spanNum: number;
  amountNum: number;
  spanChanged: boolean;
  wasSpanDays: number | undefined;
  previousRate: number | null;
  t: TFunction;
}

/** "Should last about" — how long the amount on screen is expected to cover,
 *  and what that implies per day against the material's lead time. */
export function StockEntrySpanField({
  value,
  onChange,
  highlighted,
  estimatedSpan,
  ratePreview,
  unitLabel,
  lead,
  unsustainable,
  spanNum,
  amountNum,
  spanChanged,
  wasSpanDays,
  previousRate,
  t,
}: IStockEntrySpanFieldProps) {
  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor="stock-span">
          {t('logistics.stockDialog.lasts')}
        </label>
        <InfoTip text={t('logistics.stockDialog.lastsInfo')} />
      </div>
      <div className={styles.spanRow}>
        <input
          id="stock-span"
          /* A sync span is derived from the merge, not from a prop, so
             it is seeded whenever the derivation is still driving it. */
          className={`${styles.input} ${styles.spanInput}${highlighted ? ` ${styles.prefilled}` : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
        />
        <span className={styles.spanCaption}>
          {t('logistics.stockDialog.days')}
          {ratePreview != null ? (
            <>
              {' → '}
              <b>{t('logistics.stockDialog.ratePreview', { rate: ratePreview, unit: unitLabel })}</b>
            </>
          ) : null}
        </span>
      </div>
      {/* The span the server takes: whole days, 1–365 (`stockSaveValid`).
          Said here so a refused span is not just a greyed-out Save. */}
      {value.trim() !== '' && !(Number.isInteger(spanNum) && spanNum >= 1 && spanNum <= 365) ? (
        <span className={styles.fieldWarn} role="alert">
          {t('logistics.stockDialog.spanInvalid')}
        </span>
      ) : null}
      {/* OUTSIDE .spanRow. That row is `display: flex`, so a note left
          inside it rendered inline beside the rate preview — the only
          field where the note did not get its own line. */}
      {/* Replaces the plain "Estimated N" note for this field — same
          job, and it also says what that estimate meant per day. */}
      {/* What the figure means and what it is up against — the two
          facts needed to judge it, neither of which the row showed. */}
      {lead != null && ratePreview != null && !unsustainable ? (
        <span className={styles.seedHint}>
          {t('logistics.stockDialog.spanWhy', {
            amount: amountNum,
            unit: unitLabel,
            rate: ratePreview,
            lead,
          })}
        </span>
      ) : null}
      {unsustainable ? (
        <span className={styles.fieldWarn}>
          {t('logistics.stockDialog.unsustainable', { span: spanNum, lead })}
        </span>
      ) : null}
      {/* "Estimated N" — amber, our suggested span, shown once the user moves
          the field off it. Matches how Lead surfaces its overridden estimate. */}
      {estimatedSpan != null ? (
        <span className={styles.seedNote}>
          {t('logistics.stockDialog.wasEstimated', { value: estimatedSpan })}
        </span>
      ) : null}
      {spanChanged ? (
        <span className={styles.wasNote}>
          {t('logistics.stockDialog.wasSpan', {
            days: wasSpanDays,
            rate: previousRate,
            unit: unitLabel,
          })}
        </span>
      ) : null}
    </div>
  );
}
